import './history.css';

import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { allUserConfigsState } from '../../recoil/atoms/allUserConfigsState';
import { teamsState } from '../../recoil/atoms/teamsState';

import { translate } from '../../../common/language/translations';
import { PlayerReport } from '../../types/report';
import { Activity } from '../../types/activity';
import { PlayerId } from '../../types/player';
import { getDisplayPlayerName, getShortUserName, getDisplayTeamName } from '../../utils/nameUtils';
import { shouldShowDateForHistoryEntry } from '../../utils/historyUtils';
import { dateStringToTextFormat } from '../../utils/dateUtils';
import { getReportIcon } from '../../utils/iconUtils';


interface PlatformHistoryProps {
  history: (Activity | PlayerReport)[];
  handlePlayerClick: (playerId: PlayerId, playerName: string, report?: PlayerReport) => void;
  handleTeamClick: (teamId: string) => void;
}

export const PlatformHistory: React.FC<PlatformHistoryProps> = ({
  history,
  handlePlayerClick,
  handleTeamClick,
}) => {

  const userConfig = useRecoilValue(userConfigState);
  const allUsersWithSameTenant = useRecoilValue(allUserConfigsState);

  const teams = useRecoilValue(teamsState);


  const getHistoryInfoText = (action: Activity | PlayerReport, isCurrentUser: boolean) => {
    if (!userConfig) return <div />;

    const userName = isCurrentUser
      ? translate('you', userConfig.language)
      : allUsersWithSameTenant[action.userEmail]
        ? getShortUserName(allUsersWithSameTenant[action.userEmail].name)
        : translate('unknownUser', userConfig.language);

    if ('reportType' in action) {

      const actionText = action.reportType === 'Attachment'
        ? translate('<someone>uploadedAnAttachment', userConfig.language)
        : translate('<someone>leftA<reportType>', userConfig.language) + ' ' + translate(action.reportType, userConfig.language, true);

      const text = userName
        + ' '
        + actionText
        + ' '
        + translate('<leftAReport>on<player>', userConfig.language)
        + ' '
        + getDisplayPlayerName(action.playerName, 16);

      return (
        <div className='platform-history-text-clickable' onClick={() => handlePlayerClick(action.playerId, action.playerName, action)}>
          {text}
        </div>
      );
    }

    const teamName = action.newName ?? getDisplayTeamName(action.teamId, teams, userConfig?.language, action.teamName);
    const teamExists = teams?.[action.teamId] !== undefined;
    const teamComponent = (
      <div
        className={'platform-history-text-nowrap' + (teamExists ? ' platform-history-text-clickable' : '')}
        onClick={() => teamExists ? handleTeamClick(action.teamId) : undefined}
      >
        {teamName}
      </div>
    );

    const containerClassName = 'platform-history-text' + (!isCurrentUser ? ' platform-history-text-other-user' : '');

    const playerId = action.playerId;
    const playerName = action.playerName;

    if (playerId !== undefined && playerName !== undefined) {

      const playerNameComponent = (
        <div
          className='platform-history-text-clickable platform-history-text-nowrap'
          onClick={() => handlePlayerClick(playerId, playerName)}
        >
          {getDisplayPlayerName(playerName, 16)}
        </div>
      );

      if (action.actionType === 'playerAdded') {
        return (
          <div className={containerClassName}>
            {userName}
            &nbsp;
            {translate('<someone>added<player>', userConfig.language)}
            {playerNameComponent}
            {translate('on<team>', userConfig.language)}
            {teamComponent}
          </div>
        );
      }

      if (action.actionType === 'playerRemoved') {
        return (
          <div className={containerClassName}>
            {userName}
            &nbsp;
            {translate('<someone>removed<player>', userConfig.language)}
            {playerNameComponent}
            {translate('from<team>', userConfig.language)}
            {teamComponent}
          </div>
        );
      }

      return null;
    }


    if (action.actionType === 'teamCreated') {
      return (
        <div className={containerClassName}>
          {userName}
          &nbsp;
          {translate('<someone>created<team>', userConfig.language)}
          {teamComponent}
        </div>
      );
    }

    if (action.actionType === 'teamArchived') {
      return (
        <div className={containerClassName}>
          {userName}
          &nbsp;
          {translate('<someone>archived<team>', userConfig.language)}
          {teamComponent}
        </div>
      );
    }

    if (action.actionType === 'teamDeleted') {
      return (
        <div className={containerClassName}>
          {userName}
          &nbsp;
          {translate('<someone>deletedTheTeam<team>', userConfig.language)}
          {teamComponent}
        </div>
      );
    }

    if (action.actionType === 'teamCommented') {
      return (
        <div className={containerClassName}>
          {userName}
          &nbsp;
          {translate('<someone>leftA<reportType>', userConfig.language)}
          &nbsp;
          {translate('comment', userConfig.language)}
          &nbsp;
          {translate('on<team>', userConfig.language)}
          {teamComponent}
          {'- '}
          {action.comment}
        </div>
      );
    }


    if (action.actionType === 'teamFormationEdited') {
      return (
        <div className={containerClassName}>
          {userName}
          &nbsp;
          {translate('<someone>editedTheFormationFor<team>', userConfig.language)}
          {teamComponent}
          {translate('<fromSomething>to<something>', userConfig.language)}
          &nbsp;
          {action.newFormation}
        </div>
      );
    }

    if (action.actionType === 'teamNameEdited') {
      return (
        <div className={containerClassName}>
          {userName}
          &nbsp;
          {translate('<someone>editedTeamNameFrom<team>', userConfig.language)}
          &nbsp;
          {action.oldName}
          &nbsp;
          {translate('<fromSomething>to<something>', userConfig.language)}
          {teamComponent}
        </div>
      );
    }

    return null;
  };


  const scrollToBottom = () => {
    const commentField = document.querySelector('.platform-history-action-section');
    if (commentField) {
      commentField.scrollTop = commentField.scrollHeight;
    }
  };


  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToBottom();
    }, 100);

    return () => clearTimeout(timer);
  }, [history]);


  return (
    <div className='history-container'>

      <div className={'history-action-section history-action-section-show platform-history-action-section'}>
        {history.map((action, index) => {

          const isReport = 'reportType' in action;
          const isCurrentUser = userConfig !== null && action.userEmail === userConfig.email;

          return (
            <div
              key={index}
              className='history-action platform-history-action'
            >

              {shouldShowDateForHistoryEntry(index, history) && (
                <div className='history-date-row platform-history-date-row'>

                  {isCurrentUser && <div className='history-date-large-divider'>&nbsp;</div>}

                  <div className={'history-date-large' + (isCurrentUser ? ' history-date-current-user' : '')}>
                    {dateStringToTextFormat(action.date.split('T')[0])}
                  </div>

                  {!isCurrentUser && <div className='history-date-large-divider'>&nbsp;</div>}

                </div>
              )}

              <div
                className={
                  'platform-history-info-text'
                    + (isCurrentUser ? ' platform-history-info-text-current-user' : '')
                    + (isReport
                      ? (isCurrentUser ? ' platform-history-report-info-current-user' : ' platform-history-report-info-other-user')
                      : '')
                }
              >
                <div className='platform-history-text-container'>
                  {getHistoryInfoText(action, isCurrentUser)}

                  {isReport && (
                    <div
                      className={
                        'platform-history-report-icon' +
                          (isCurrentUser ? ' platform-history-report-icon-current-user' : ' platform-history-report-icon-other-user')
                      }
                    >
                      <div style={{ paddingLeft: isCurrentUser ? 8 : undefined, paddingRight: isCurrentUser ? undefined : 8 }}>
                        {getReportIcon(action.reportType, 21, '#ffffffbb')}
                      </div>
                    </div>
                  )}
                </div>
              </div>

            </div>
          );
        })}
      </div>

    </div>
  );
};
